import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ConfirmDialog from 'components/ui/ConfirmDialog';
import Toast from 'components/ui/Toast';
import { HRMS_API_ERROR_EVENT } from 'utils/apiError';

const noop = () => {};
const PopupContext = createContext({ toast: noop, confirm: () => Promise.resolve(false) });

let _toastId = 0;

export function PopupProvider({ children }) {
  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const recentToastsRef = useRef(new Map());

  const removeToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);

  const toast = useCallback((message, type = 'info') => {
    const normalizedMessage = String(message || '').trim();
    if (!normalizedMessage) return;

    const dedupeKey = `${type}:${normalizedMessage}`;
    const now = Date.now();
    const lastShownAt = recentToastsRef.current.get(dedupeKey) || 0;
    if (now - lastShownAt < 1500) return;
    recentToastsRef.current.set(dedupeKey, now);

    const id = ++_toastId;
    setToasts((p) => [...p, { id, message: normalizedMessage, type }]);
  }, []);

  useEffect(() => {
    function handleApiError(event) {
      const msg = event?.detail?.message;
      if (msg && String(msg).toLowerCase().includes('bearer token')) {
        return;
      }
      toast(msg || 'Something went wrong', 'error');
    }

    window.addEventListener(HRMS_API_ERROR_EVENT, handleApiError);
    return () => window.removeEventListener(HRMS_API_ERROR_EVENT, handleApiError);
  }, [toast]);

  // ── Confirm ────────────────────────────────────────────────────────────────
  const [dialog, setDialog] = useState({ open: false });
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    const { title, message, confirmLabel, cancelLabel, severity } = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({ open: true, title, message, confirmLabel, cancelLabel, severity });
    });
  }, []);

  function handleConfirm() { setDialog({ open: false }); resolveRef.current?.(true);  }
  function handleCancel()  { setDialog({ open: false }); resolveRef.current?.(false); }

  return (
    <PopupContext.Provider value={{ toast, confirm }}>
      {children}
      <ConfirmDialog {...dialog} onConfirm={handleConfirm} onCancel={handleCancel} />
      <Toast toasts={toasts} onRemove={removeToast} />
    </PopupContext.Provider>
  );
}

export function useToast()   { return useContext(PopupContext).toast;   }
export function useConfirm() { return useContext(PopupContext).confirm; }
